import { useState } from 'react';
import axios from 'axios';
import { theme, buttonStyles, inputStyles, modalStyles, typography, spacing, mergeStyles } from '../styles/theme';

export default function FlagModal({ question, botResponse, user, onClose }) {
  const [userComment, setUserComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Get Firebase token
      const token = user ? await user.getIdToken() : null;

      if (!token) {
        console.error('Must be logged in to submit feedback.');
        return;
      }

      // Send to backend
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/feedback`,
        {
          question,
          botResponse,
          userComment: userComment.trim() || undefined  // Send undefined if empty
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(true);
      
      // Auto-close after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          ...modalStyles.content,
          maxWidth: '600px'
        }}
      >
        {/* Close button */}
        <button onClick={onClose} style={modalStyles.closeButton}>
          ×
        </button>

        {/* Success message */}
        {success ? (
          <div style={{ textAlign: 'center', padding: spacing['2xl'] }}>
            <h2 style={{ 
              color: theme.status.success,
              fontFamily: typography.fontFamily.primary,
              marginBottom: spacing.md
            }}>
              Thank you!
            </h2>
            <p style={{ color: theme.text.primary }}>
              Your feedback has been submitted.
            </p>
          </div>
        ) : (
          <>
            <h2 style={{
              marginTop: 0,
              color: theme.text.primary,
              fontFamily: typography.fontFamily.primary,
              fontSize: typography.fontSize.xl
            }}>
              Flag This Answer
            </h2>

            <p style={{ 
              color: theme.text.secondary,
              fontSize: typography.fontSize.sm,
              marginBottom: spacing.lg
            }}>
              Help us improve! Let us know why this answer might be incorrect.
            </p>

            {/* Show the question and answer being flagged */}
            <div style={{
              backgroundColor: theme.bg.secondary,
              padding: spacing.lg,
              borderRadius: '8px',
              marginBottom: spacing.lg,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <strong style={{ color: theme.text.primary }}>Question:</strong>
              <p style={{ 
                margin: `${spacing.sm} 0`,
                color: theme.text.primary 
              }}>
                {question}
              </p>
              
              <strong style={{ color: theme.text.primary }}>Bot's Answer:</strong>
              <p style={{ 
                margin: `${spacing.sm} 0`,
                color: theme.text.primary,
                fontSize: typography.fontSize.sm
              }}>
                {botResponse.substring(0, 300)}...
              </p>
            </div>

            {/* Error message */}
            {error && (
              <p style={{
                color: theme.status.error,
                backgroundColor: '#FFE5E5',
                padding: spacing.md,
                borderRadius: '6px',
                fontSize: typography.fontSize.sm,
                marginBottom: spacing.lg
              }}>
                {error}
              </p>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <label style={{
                display: 'block',
                marginBottom: spacing.sm,
                color: theme.text.primary,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium
              }}>
                What's wrong with this answer? (optional)
              </label>
              
              <textarea
                value={userComment}
                onChange={(e) => setUserComment(e.target.value)}
                placeholder="E.g., 'The bot missed rule 704.5g about state-based actions...'"
                rows="4"
                style={{
                  ...inputStyles.base,
                  resize: 'vertical',
                  fontFamily: typography.fontFamily.primary,
                  marginBottom: spacing.lg
                }}
              />

              {/* Buttons */}
              <div style={{ display: 'flex', gap: spacing.md }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  style={mergeStyles(
                    buttonStyles.base,
                    buttonStyles.secondary,
                    { flex: 1 }
                  )}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={submitting}
                  style={mergeStyles(
                    buttonStyles.base,
                    submitting ? buttonStyles.disabled : buttonStyles.primary,
                    { flex: 1 }
                  )}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}